import { useNavigate, useSearch } from "@tanstack/react-router";
import React from "react";

import type {
  TestRunDetails,
  TestRunWorkspaceDetails,
  WorkspaceAccession,
  WorkspaceInstanceMaterial,
  WorkspaceSample,
  WorkspaceTestOrderSample,
  WorkspaceTestSpec,
} from "../types";

type WorkspaceProviderContextType = {
  materials: TestRunWorkspaceDetails["materials"];
  workflow: TestRunWorkspaceDetails["process"];
  testRun: TestRunDetails;
  accessions: WorkspaceAccession[];
  samples: WorkspaceSample[];
  testOrderSamples: WorkspaceTestOrderSample[];
  instanceMaterials: WorkspaceInstanceMaterial[];
  testSpec: WorkspaceTestSpec;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  activeDay: number | null;
  setActiveDay: (day: number | null) => void;
  updateProcess: boolean;
  setUpdateProcess: (updateProcess: boolean) => void;
};

const WorkspaceProviderContext = React.createContext(
  {} as WorkspaceProviderContextType,
);

export function WorkspaceProvider({
  children,
  workspace,
  testRun,
}: {
  children: React.ReactNode;
  workspace: TestRunWorkspaceDetails;
  testRun: TestRunDetails;
}) {
  const [updateProcess, setUpdateProcess] = React.useState(false);
  const navigate = useNavigate();

  const { tab: activeTab, day: activeDay } = useSearch({
    from: "/_auth/_app/runs/$id/workspace",
  });

  const setActiveTab = React.useCallback(
    (tab: string) => {
      navigate({
        to: "/runs/$id/workspace",
        params: { id: testRun.id },
        search: { tab, day: tab === "OBSERVATION" ? activeDay : null },
      });
    },
    [navigate, testRun.id, activeDay],
  );

  const setActiveDay = React.useCallback(
    (day: number | null) => {
      navigate({
        to: "/runs/$id/workspace",
        params: { id: testRun.id },
        search: { tab: "OBSERVATION", day },
      });
    },
    [navigate, testRun.id],
  );

  return (
    <WorkspaceProviderContext.Provider
      value={{
        activeTab,
        setActiveTab,
        activeDay,
        setActiveDay,
        testRun,
        ...workspace,
        workflow: workspace.process,
        updateProcess,
        setUpdateProcess,
      }}
    >
      {children}
    </WorkspaceProviderContext.Provider>
  );
}

export function useWorkspace() {
  const context = React.useContext(WorkspaceProviderContext);
  if (!context) {
    throw new Error(
      "useTestRunWorkspace must be used within a TestRunWorkspaceProvider",
    );
  }
  return context;
}
